import type { SubmitReviewByExpertIdBody } from '@shopexperts/shared';

type OrganicReviewValues = Omit<SubmitReviewByExpertIdBody, 'expert_id' | 'idempotency_key'>;

type ApiErrorLike = {
  message?: string;
  response?: {
    status?: number;
    data?: {
      code?: string;
      message?: string;
      errors?: Record<string, string[]>;
    };
  };
};

export function createDirectReviewFingerprint(expertId: number, values: OrganicReviewValues): string {
  return JSON.stringify({
    expert_id: expertId,
    quality: values.quality,
    communication: values.communication,
    timelines: values.timelines,
    value_for_money: values.value_for_money,
    review_text: values.review_text.trim(),
    is_anonymous: values.is_anonymous ?? false,
    project_id: values.project_id ?? null,
    project_value_range_id: values.project_value_range_id ?? null,
  });
}

export function createDirectReviewIdempotencyKey(): string {
  if (typeof globalThis.crypto?.randomUUID === 'function') {
    return globalThis.crypto.randomUUID();
  }

  return `direct-review-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export function getDirectReviewErrorMessage(error: unknown): string {
  const apiError = error as ApiErrorLike;
  const status = apiError?.response?.status;
  const data = apiError?.response?.data;
  const code = data?.code;

  const fieldErrors = Object.values(data?.errors ?? {})
    .flat()
    .filter((message): message is string => typeof message === 'string' && message.length > 0);

  let detail = fieldErrors.join(' ');
  if (!detail) detail = data?.message ?? '';
  if (!detail && status === 401) detail = 'Your session expired. Please log in and try again.';
  if (!detail) detail = apiError?.message ?? '';
  if (!detail) detail = 'The review could not be submitted. Please try again.';

  const reference = code || (status ? `HTTP ${status}` : '');
  return reference ? `${detail} (${reference})` : detail;
}

export function getSafeClientRedirect(roleId: number | undefined, redirect: unknown): string | null {
  if (roleId !== 2 || typeof redirect !== 'string') return null;
  if (redirect.includes('\\') || redirect.startsWith('//')) return null;
  return redirect === '/client' || redirect.startsWith('/client/') ? redirect : null;
}
